import { CheckIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Modal } from 'stories/components'

type Function = () => void

interface PromptModalProps {
  /**
   * Title
   */
  title?: string | JSX.Element
  /**
   * Okay button handler
   */
  onOk?: (arg: any) => void
  /**
   * Cancel button handler
   */
  onCancel?: Function
  /**
   * Is Open
   */
  isOpen?: boolean
  lastUpdatedAt: number
  options?: { name: string; value: any; desc?: string }[]
  selected?: number
  multi?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const ConfirmOptionsModal = ({
  title = '',
  onOk = () => {},
  onCancel = () => {},
  isOpen: parentIsOpen = false,
  lastUpdatedAt,
  options = [],
  selected,
  multi = false,
}: PromptModalProps) => {
  const [showModal, setShowModal] = useState(false)
  const [value, setValue] = useState<any>()
  const [selectedIndex, setSelectedIndex] = useState<Number>(-1)
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([])
  const [selectedValues, setSelectedValues] = useState<any[]>([])

  useEffect(() => {
    setShowModal(parentIsOpen)
    setSelectedIndexes([])
    setSelectedValues([])
    setSelectedIndex(-1)
    setValue(undefined)
    if (selected != null) {
      setSelectedIndex(selected)
      setValue(options[selected]?.value)
      if (multi) {
        setSelectedIndexes([selected])
        setSelectedValues([options[selected]?.value])
      }
    }
  }, [lastUpdatedAt])

  if (!showModal) return null

  const isSelected = (index: number) => (multi ? selectedIndexes.includes(index) : selectedIndex == index)
  const isDisabled = multi ? selectedIndexes.length === 0 : selectedIndex == -1

  const onClickOption = (item: { name: string; value: any; desc?: string }, index: number) => {
    if (!multi) {
      setValue(item.value)
      setSelectedIndex(index)
      return
    }
    const nextIndexes = selectedIndexes.includes(index)
      ? selectedIndexes.filter((i) => i !== index)
      : [...selectedIndexes, index]
    const nextValues = nextIndexes.map((i) => options[i]?.value)
    setSelectedIndexes(nextIndexes)
    setSelectedValues(nextValues)
  }

  return (
    <>
      <Modal
        title={title}
        titleOkay="Okay"
        loading={false}
        isOpen={parentIsOpen}
        lastUpdatedAt={lastUpdatedAt}
        onClose={onCancel}
        onOk={() => onOk(multi ? selectedValues : (value as any))}
        childLevel={2}
        disabled={isDisabled}
      >
        <div className="min-w-[32rem]">
          {options.map((item, index: number) => (
            <div
              className={`border rounded-md overflow-hidden p-4 ${
                index != options.length - 1 && 'mb-4'
              } cursor-pointer hover:border-shade-blue ${isSelected(index) ? 'border-shade-blue' : ''}`}
              onClick={() => onClickOption(item, index)}
              key={index}
            >
              <div className="flex justify-between items-center">
                <p className="font-semibold">
                  {index + 1}. {item?.name}
                </p>
                {isSelected(index) ? <CheckIcon className="w-5 h-5 text-shade-blue" /> : null}
              </div>
              <p className="text-xs mb-1 underline">{item?.desc}</p>
            </div>
          ))}
        </div>
      </Modal>
    </>
  )
}
